import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sorted from '../components/Sorted';
import { SearchProvider } from '../contexts/SearchContext';
import SearchInput from '../components/SearchInput';
import SearchResults from '../components/SearchResults';
import ItemRow from '../components/ItemRow';
import BlurFillImage from '../components/BlurFillImage';
import PlusIcon from '../components/icons/PlusIcon';
import useAuth from '../contexts/useAuth';
import useToast from '../contexts/useToast';
import type { ListData } from '../../../types';

interface PlaylistSearchProps {
  contentIds: string[];
  onAddItem: (item: ListData) => void;
  isAdmin?: boolean;
}

const PlaylistSearch = ({
  contentIds,
  onAddItem,
  isAdmin,
}: PlaylistSearchProps) => {
  return (
    <>
      <SearchInput placeholder="Search or paste a URL..." />
      <SearchResults
        excludeIds={contentIds}
        renderResult={(item) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              margin: '0 10px',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <ItemRow data={item} noMargin isAdmin={isAdmin} />
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddItem(item);
              }}
              disabled={item.status === 'failed'}
              className="pill-button"
              style={{
                padding: '8px',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: item.status === 'failed' ? 0.5 : 1,
              }}
              title="Add to playlist"
            >
              <PlusIcon size={24} color="var(--gold)" />
            </button>
          </div>
        )}
      />
    </>
  );
};

const PlaylistEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.isAdmin;

  // Playlist metadata
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [contentIds, setContentIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [itemsVersion, setItemsVersion] = useState(0);

  // Track original values to detect changes
  const originalValues = useRef({ title: '', description: '', imageUrl: '' });

  // Create playlist if navigating to /playlist/new
  useEffect(() => {
    if (!id) {
      // Create new playlist and redirect
      const createPlaylist = async () => {
        try {
          const res = await fetch('/api/playlists/create', {
            method: 'POST',
            credentials: 'include',
          });
          if (!res.ok) throw new Error('Failed to create playlist');
          const playlist = await res.json();
          navigate(`/playlist/edit/${playlist.id}`, { replace: true });
        } catch {
          showToast('Failed to create playlist', 'error');
          navigate(-1);
        }
      };
      createPlaylist();
    }
  }, [id, navigate, showToast]);

  // Load playlist data
  useEffect(() => {
    if (!id) return;

    const loadPlaylist = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`/api/content/${id}`, {
          credentials: 'include',
        });
        const data = await res.json();

        if (data.type !== 'playlist') {
          showToast('Not a playlist', 'error');
          navigate('/');
          return;
        }

        if (data.createdBy !== user?.id) {
          showToast('You can only edit your own playlists', 'error');
          navigate(`/id/${id}`);
          return;
        }

        setTitle(data.title || '');
        setDescription(data.description || '');
        setImageUrl(data.imageUrl || '');
        setContentIds(JSON.parse(data.contentIds || '[]'));

        originalValues.current = {
          title: data.title || '',
          description: data.description || '',
          imageUrl: data.imageUrl || '',
        };
      } catch {
        showToast('Failed to load playlist', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlaylist();
  }, [id, navigate, showToast, user?.id]);

  // Save field on blur if changed
  const saveField = useCallback(
    async (field: 'title' | 'description' | 'imageUrl', value: string) => {
      if (!id) return;
      const originalValue = originalValues.current[field];
      if (value === originalValue) return;

      // Don't save empty title
      if (field === 'title' && !value.trim()) {
        setTitle(originalValue);
        return;
      }

      try {
        const res = await fetch('/api/playlists/update', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, [field]: value.trim() || null }),
        });
        if (!res.ok) throw new Error('Failed to save');
        originalValues.current[field] = value;
      } catch {
        showToast('Failed to save', 'error');
        // Revert to original
        if (field === 'title') setTitle(originalValue);
        if (field === 'description') setDescription(originalValue);
        if (field === 'imageUrl') setImageUrl(originalValue);
      }
    },
    [id, showToast]
  );

  // Add item to playlist
  const addItem = useCallback(
    async (item: ListData) => {
      if (!id) return;
      try {
        const res = await fetch('/api/playlists/items/add', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, contentId: item.id }),
        });
        if (!res.ok) throw new Error('Failed to add item');
        const data = await res.json();
        setContentIds(data.contentIds);
        setItemsVersion((v) => v + 1);
      } catch {
        showToast('Failed to add item', 'error');
      }
    },
    [id, showToast]
  );

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  if (!id || isLoading) {
    return (
      <div className="page">
        <p style={{ textAlign: 'center', padding: '40px' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header with back button and title */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 10px 0 10px',
        }}
      >
        <button
          onClick={handleBack}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--primary)',
            width: '80px',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <span
          style={{
            fontWeight: 600,
            fontSize: '1.1rem',
            color: 'var(--primary)',
          }}
        >
          Edit Playlist
        </span>
        <div style={{ width: '80px' }} />
      </div>

      {/* Form fields */}
      <div style={{ padding: '10px', marginBottom: '10px' }}>
        <div style={{ marginBottom: '12px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              color: 'var(--primary-soft)',
            }}
          >
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => saveField('title', title)}
            placeholder="Playlist title"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              background: 'var(--card-bg)',
              color: 'var(--primary)',
              fontSize: '1rem',
            }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              color: 'var(--primary-soft)',
            }}
          >
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => saveField('description', description)}
            placeholder="A collection of..."
            rows={2}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              background: 'var(--card-bg)',
              color: 'var(--primary)',
              fontSize: '1rem',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              color: 'var(--primary-soft)',
            }}
          >
            Cover Image URL
          </label>
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onBlur={() => saveField('imageUrl', imageUrl)}
            placeholder="https://..."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              background: 'var(--card-bg)',
              color: 'var(--primary)',
              fontSize: '1rem',
            }}
          />
          {imageUrl.trim() && (
            <div
              style={{
                marginTop: '8px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <BlurFillImage
                imageUrl={imageUrl.trim()}
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Playlist items */}
      <p className="section-title">Items ({contentIds.length})</p>
      <Sorted<ListData>
        route={`/api/playlists/items/${id}`}
        render={(item) => <ItemRow data={item} noMargin isAdmin={isAdmin} />}
        deps={[itemsVersion]}
        emptyMessage="No items yet"
        onRemove={async (item) => {
          await fetch('/api/playlists/items/remove', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, contentId: item.id }),
          });
          setContentIds((prev) => prev.filter((cid) => cid !== item.id));
        }}
        onReorder={async (items) => {
          const newContentIds = items.map((item) => item.id);
          setContentIds(newContentIds);
          await fetch('/api/playlists/items/reorder', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, contentIds: newContentIds }),
          });
        }}
      />

      <p className="section-title" style={{ marginTop: '20px' }}>
        Add Content
      </p>
      <SearchProvider>
        <PlaylistSearch
          contentIds={contentIds}
          onAddItem={addItem}
          isAdmin={isAdmin}
        />
      </SearchProvider>
    </div>
  );
};

export default PlaylistEditor;
